import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { SEO } from '../components/SEO';
import Magnetic from '../components/Magnetic';
import Carousel from '../components/Carousel';
import { EASE, ParallaxImg, RevealWord, fadeUp } from '../components/motion';

/* ------------------------------------------------------------------- data */

const values = [
  'Drive innovation through business excellence and strategic thinking.',
  'Deliver exceptional value and measurable results for our clients.',
  'Foster collaborative partnerships built on trust and transparency.',
  'Embrace continuous learning and professional growth.',
  'Maintain the highest standards of quality in every project.',
  'Promote sustainable and responsible business practices.',
  'Celebrate diversity and cultural understanding.',
];

const approach = [
  {
    title: 'Market Insight',
    description:
      'Deep understanding of diverse South African markets and business landscapes.',
  },
  {
    title: 'Strategic Innovation',
    description: 'Cutting-edge solutions that blend local knowledge with global trends.',
  },
  {
    title: 'Client Success',
    description: 'Dedicated to helping businesses thrive across South Africa.',
  },
];

/* ------------------------------------------------------------------- page */

const About = () => {
  return (
    <>
      <SEO
        title="About DKSN Business Solutions | Mthatha's Leading Business Services"
        description="Learn about DKSN Business Solutions, Mthatha's trusted provider of business registration, branding, and printing services. Discover our journey and commitment to excellence."
        path="/about"
      />
      <div className="min-h-screen bg-primary-950 text-white">
        {/* ============================================== HERO — manifesto */}
        <section className="relative overflow-hidden pb-20 pt-40 sm:pb-28 sm:pt-48">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(900px_500px_at_15%_10%,rgba(91,141,239,0.13),transparent_60%)]" />
          <div className="section-container relative">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: EASE }}
              className="mb-6 font-display text-sm font-semibold tracking-[0.12em] text-azure-500"
            >
              ABOUT DKSN — SINCE 2020
            </motion.div>
            <h1 className="mb-10 max-w-4xl font-display text-5xl font-bold leading-[1.02] tracking-[-0.045em] sm:text-6xl lg:text-7xl xl:text-[5.5rem]">
              <RevealWord delay={0.12}>Small businesses deserve</RevealWord>
              <br />
              <RevealWord delay={0.26}>
                to look like{' '}
                <span className="font-serif font-normal italic text-azure-300">big ones.</span>
              </RevealWord>
            </h1>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.55, ease: EASE }}
              className="grid max-w-4xl gap-10 sm:grid-cols-2 sm:gap-16"
            >
              <p className="text-[17.5px] leading-[1.7] text-gray-300">
                DKSN Business Solutions is a leading provider of comprehensive business services
                in South Africa. From Shop 23B at the Mall of Mthatha, we help businesses
                register, brand, print — and thrive in a competitive market.
              </p>
              <p className="text-[17.5px] leading-[1.7] text-gray-500">
                Our commitment to excellence and deep understanding of the South African business
                landscape sets us apart — tailored solutions that drive real results, not
                templates.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ================================== FULL-BLEED STUDIO IMAGE */}
        <section className="relative h-[64vh] min-h-[440px] overflow-hidden">
          <ParallaxImg
            src="https://images.unsplash.com/photo-1739298061740-5ed03045b280?w=2000&auto=format&fit=crop&q=75"
            alt="The DKSN team working with a client"
            speed={20}
            className="absolute inset-0"
            overlayClassName="bg-gradient-to-b from-primary-950 via-transparent to-primary-950"
          />
        </section>

        {/* ====================================== VISION / MISSION — panels */}
        <section className="section-container pt-16 sm:pt-36">
          <div className="grid gap-4 lg:grid-cols-2">
            <motion.div
              {...fadeUp}
              className="relative overflow-hidden rounded-3xl border border-white/[0.09] bg-gradient-to-br from-azure-500/[0.09] to-azure-500/[0.02] p-10 sm:p-14"
            >
              <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-azure-500/20 blur-3xl" />
              <span className="relative text-sm font-semibold uppercase tracking-[0.14em] text-azure-400">
                Our Vision
              </span>
              <p className="relative mt-7 font-serif text-2xl italic leading-[1.4] tracking-[-0.01em] text-white sm:text-3xl">
                To be the catalyst for business transformation through innovative solutions and
                strategic excellence — setting new standards in business solutions.
              </p>
            </motion.div>
            <motion.div
              {...fadeUp}
              transition={{ duration: 1, delay: 0.12, ease: EASE }}
              className="rounded-3xl border border-white/[0.09] bg-white/[0.02] p-10 sm:p-14"
            >
              <span className="text-sm font-semibold uppercase tracking-[0.14em] text-azure-400">
                Our Mission
              </span>
              <p className="mt-7 font-serif text-2xl italic leading-[1.4] tracking-[-0.01em] text-white sm:text-3xl">
                To empower businesses with comprehensive solutions that drive growth, foster
                innovation, and create lasting impact in an ever-evolving market.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ========================================= VALUES — numbered list */}
        <section className="section-container pt-16 sm:pt-36">
          <div className="grid gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
            <motion.div {...fadeUp} className="lg:sticky lg:top-32 lg:self-start">
              <span className="text-sm font-semibold uppercase tracking-[0.14em] text-azure-400">
                What Guides Us
              </span>
              <h2 className="mt-5 font-display text-4xl font-bold leading-[1.02] tracking-[-0.04em] sm:text-5xl lg:text-6xl">
                Seven things we{' '}
                <span className="font-serif font-normal italic text-azure-300">
                  refuse to compromise.
                </span>
              </h2>
              <p className="mt-6 max-w-sm leading-relaxed text-gray-500">
                Our core values aren't wall art. They're the checklist every project passes
                before it leaves the studio.
              </p>
            </motion.div>
            <div>
              {values.map((value, i) => (
                <motion.div
                  key={value}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.7, delay: (i % 4) * 0.06, ease: EASE }}
                  className={`flex items-baseline gap-7 border-t border-white/10 py-7 ${
                    i === values.length - 1 ? 'border-b' : ''
                  }`}
                >
                  <span className="flex-shrink-0 font-display text-sm font-semibold tracking-[0.08em] text-azure-500">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <p className="text-lg leading-[1.55] text-gray-300 sm:text-xl">{value}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ======================================== APPROACH — three pillars */}
        <section className="section-container pt-16 sm:pt-36">
          <motion.div {...fadeUp} className="mb-14">
            <span className="text-sm font-semibold uppercase tracking-[0.14em] text-azure-400">
              How We Work
            </span>
            <h2 className="mt-5 font-display text-4xl font-bold leading-[1.02] tracking-[-0.04em] sm:text-5xl lg:text-6xl">
              Local knowledge,{' '}
              <span className="font-serif font-normal italic text-azure-300">
                global standards.
              </span>
            </h2>
          </motion.div>
          <Carousel slideClassName="w-[80%]" desktopGridClassName="sm:grid sm:grid-cols-3 sm:gap-4">
            {approach.map((item) => (
              <div key={item.title} className="h-full rounded-[22px] border border-white/[0.09] bg-white/[0.02] p-9">
                <h3 className="mb-3 font-display text-xl font-bold tracking-[-0.02em] text-white">
                  {item.title}
                </h3>
                <p className="text-[15px] leading-relaxed text-gray-500">{item.description}</p>
              </div>
            ))}
          </Carousel>
        </section>

        {/* =========================================== VISIT — location band */}
        <section className="relative mt-16 overflow-hidden sm:mt-40">
          <ParallaxImg
            src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=2000&auto=format&fit=crop&q=75"
            alt="DKSN storefront at the Mall of Mthatha"
            speed={16}
            className="absolute inset-0"
            overlayClassName="bg-gradient-to-b from-primary-950 via-primary-950/85 to-primary-950/95"
          />
          <motion.div
            {...fadeUp}
            className="section-container relative grid gap-14 py-16 sm:py-36 lg:grid-cols-2 lg:items-center"
          >
            <div>
              <span className="text-sm font-semibold uppercase tracking-[0.14em] text-azure-400">
                Visit The Studio
              </span>
              <h2 className="mb-6 mt-5 font-display text-4xl font-bold leading-[1.02] tracking-[-0.045em] sm:text-5xl lg:text-6xl">
                Walk in. Walk out with a{' '}
                <span className="font-serif font-normal italic text-azure-300">plan.</span>
              </h2>
              <p className="mb-9 max-w-md text-lg leading-relaxed text-gray-300">
                No appointment needed. Bring your idea to Shop 23B and talk it through with the
                team over the counter.
              </p>
              <div className="flex flex-wrap gap-3.5">
                <Magnetic>
                  <a
                    href="https://maps.google.com/?q=Mall+of+Mthatha,+33+Errol+Spring+Ave,+Mthatha"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-2.5 rounded-full bg-gray-100 px-8 py-4 text-base font-semibold text-primary-950 shadow-[0_18px_50px_-16px_rgba(255,255,255,0.5)] transition-colors hover:bg-white"
                  >
                    Get Directions
                    <ArrowUpRight className="h-[18px] w-[18px] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                </Magnetic>
                <a
                  href="https://wa.me/27679210514"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center rounded-full border border-white/20 bg-white/5 px-7 py-4 text-base font-medium text-white backdrop-blur-md transition-colors hover:bg-white/15"
                >
                  WhatsApp Us
                </a>
              </div>
            </div>
            <div className="grid gap-3.5">
              <div className="rounded-2xl border border-white/10 bg-primary-950/55 p-7 backdrop-blur-md">
                <span className="text-xs font-semibold uppercase tracking-[0.1em] text-azure-400">
                  Address
                </span>
                <p className="mt-2 text-base leading-[1.55] text-white">
                  Shop 23B, Mall of Mthatha
                  <br />
                  33 Errol Spring Ave, Mthatha, 5099
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3.5">
                <div className="rounded-2xl border border-white/10 bg-primary-950/55 p-7 backdrop-blur-md">
                  <span className="text-xs font-semibold uppercase tracking-[0.1em] text-azure-400">
                    Call
                  </span>
                  <a href="tel:+27679210514" className="mt-2 block text-base text-white">
                    +27 67 921 0514
                  </a>
                </div>
                <div className="rounded-2xl border border-white/10 bg-primary-950/55 p-7 backdrop-blur-md">
                  <span className="text-xs font-semibold uppercase tracking-[0.1em] text-azure-400">
                    Email
                  </span>
                  <a
                    href="mailto:sales@dksnbsolutions.co.za"
                    className="mt-2 block break-all text-[15px] text-white"
                  >
                    sales@dksnbsolutions.co.za
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </section>
      </div>
    </>
  );
};

export default About;
